import PropTypes from 'prop-types';
import React from 'react';
import '../styles/QuestionsAndAnswersListItem.css';
import { useCallback } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import LoadingFallback from './LoadingFallback';
import Attachment from './Attachment';
import getHumanReadableDiff from '../helpers/getHumanReadableDiff';
import AnswerVotes from './AnswerVotes';
import FollowQuestion from './FollowQuestion';
import QuestionReplyForm from './QuestionReplyForm';

/** CONFIGURATION */
// The default number of answers to show in the component.
const DEFAULT_LIMIT = 10;

/**
 * Display a product's question and its answers.
 * @component
 */
const QuestionsAndAnswersListItem = ({ question }) => {
    const [answers, setAnswers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [currentLimit, setCurrentLimit] = useState(DEFAULT_LIMIT);
    const [answersLoaded, setAnswersLoaded] = useState(false);

    /**
     * Get question answers from the API.
     */
    const fetchAnswers = useCallback(async () => {
        try {
            setLoading(true);
            const url = new URL(`/api/answers/${question.id}`, process.env.APP_URL);
            const params = new URLSearchParams({
                page: currentPage ?? 0,
                limit: currentLimit ?? DEFAULT_LIMIT,
            });
            const response = await fetch(`${url}?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
            });
            if (!response.ok)
                throw new Error('Something went wrong.');
            const data = await response.json();
            setAnswers(data.answers);
            setLoading(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setAnswersLoaded(true);
        }
    }, [question]);

    useEffect(() => {
        if (!answersLoaded) {
            fetchAnswers();
        }
    }, [fetchAnswers]);

    return (
        <div>
            <div
                className={'question-container'}>
                <p
                    className={'qa-date-ago'}
                >
                    {getHumanReadableDiff(question.createdAt)}
                </p>
                <p>
                    <h5>{question.title}</h5>
                    <div
                        dangerouslySetInnerHTML={{ __html: question.question }}
                    />                    <span
                        className={'qanda-item--muted-text'}> ({question.name})</span>
                </p>
                <div>
                    {question.Attachments.map((attachment, index) => {
                        return (
                            <Attachment
                                key={index}
                                attachment={attachment}
                            />
                        );
                    })}
                </div>
                <FollowQuestion
                    questionId={question.id}
                />
                <QuestionReplyForm
                    questionId={question.id}
                    fetchAnswers={fetchAnswers}
                />
            </div>
            {loading && <LoadingFallback />}
            {(Array.isArray(answers) && answers.length > 0 && !loading)
                ? answers.map((answer, index) => {
                    return <div
                        className={'answer-container'}
                        key={index}>
                        <p
                            className={'qa-date-ago'}
                        >
                            {getHumanReadableDiff(answer.createdAt)}
                        </p>
                        <div
                            className={'answer-text'}
                            dangerouslySetInnerHTML={{ __html: answer.answer }}
                        >
                        </div>
                        <div>
                            {answer.Attachments.map((attachment, index) => {
                                return (
                                    <Attachment
                                        key={index}
                                        attachment={attachment}
                                    />
                                );
                            })}
                        </div>
                        <AnswerVotes
                            answerId={answer.id}
                        />
                    </div>;
                })
                : <p
                    className={'qanda-item--muted-text'}>(This question haven&apos;t been answered yet.)</p>
            }
        </div>
    );
};

/** Prop Types */
QuestionsAndAnswersListItem.propTypes = {
    /**
     * A unique eventKey for this item.
     * The eventKey is used for accordion items.
    */
    eventKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** The question to display as a list item. */
    question: PropTypes.shape({
        /** The ID of the question. */
        id: PropTypes.number,
        /** The name of the person who asked the question. */
        name: PropTypes.string,
        /** The question. */
        title: PropTypes.string,
        question: PropTypes.string,
        /** The list of images for the question. */
        Attachments: PropTypes.arrayOf(PropTypes.shape({
            /** The URL of the image. */
            url: PropTypes.string,
            /** The alt text of the image. */
            altText: PropTypes.string,
        })),
        /** createdAt date. */
        createdAt: PropTypes.string,
        /** The list of answers for the question. */
        Answers: PropTypes.arrayOf(PropTypes.shape({
            /** The answer to the question. */
            answer: PropTypes.string,
        })),
    }),
};

export default QuestionsAndAnswersListItem;
